import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { PackagePlus, Check, X } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'

const INPUT = 'w-full rounded-lg border border-brand-border bg-white px-2.5 py-1.5 text-sm focus:border-brand-primary focus:outline-none'

// OWNER-ONLY: approve a staff-suggested item (mint a catalogue StockItem) or reject it.
export default function Pending({ suggestions }) {
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex items-center gap-3">
          <PackagePlus className="text-brand-primary" />
          <div>
            <h1 className="text-xl font-semibold text-brand-ink">Pending items</h1>
            <p className="text-sm text-brand-muted">Staff-suggested items waiting to join the catalogue</p>
          </div>
        </div>
        {suggestions.length === 0 ? (
          <p className="rounded-2xl border border-brand-border bg-white px-4 py-10 text-center text-sm text-brand-muted">Nothing pending. 🎉</p>
        ) : (
          <div className="space-y-3">{suggestions.map((s) => <Card key={s.id} s={s} />)}</div>
        )}
      </div>
    </DashboardLayout>
  )
}

function Card({ s }) {
  const slug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40)
  const [f, setF] = useState({ ref: slug || `item-${s.id}`, name: s.name, supplier: '', supplier_code: '', unit: '', par_level: '', storage_area: 'Uncategorised' })
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))

  function approve() {
    if (!f.ref.trim() || !f.name.trim()) { toast.error('Ref and name are required'); return }
    setBusy(true)
    router.post(`/stock/pending/${s.id}/approve`, f, {
      onSuccess: () => toast.success(`Added ${f.name}`),
      onError: (e) => { toast.error(Object.values(e || {})[0] || 'Could not approve'); setBusy(false) },
    })
  }
  function reject() {
    if (!confirm('Reject this suggestion?')) return
    router.post(`/stock/pending/${s.id}/reject`, {}, { onSuccess: () => toast.success('Rejected') })
  }

  return (
    <div className="rounded-2xl border border-brand-border bg-white p-4">
      <div className="mb-3 flex items-start gap-3">
        {s.photo_url && <img src={s.photo_url} alt="" className="h-16 w-16 flex-shrink-0 rounded-lg object-cover" />}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-brand-ink">{s.name}</p>
          <p className="text-xs text-brand-muted">
            by {s.suggested_by || 'staff'} · {(s.created_at || '').slice(0, 16).replace('T', ' ')}
            {s.rough_qty != null && ` · ~${s.rough_qty} on hand`}
          </p>
          {s.note && <p className="mt-1 text-sm text-brand-ink">{s.note}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Lbl t="Ref (join key)"><input value={f.ref} onChange={set('ref')} className={INPUT} /></Lbl>
        <Lbl t="Name"><input value={f.name} onChange={set('name')} className={INPUT} /></Lbl>
        <Lbl t="Supplier"><input value={f.supplier} onChange={set('supplier')} className={INPUT} /></Lbl>
        <Lbl t="Supplier code"><input value={f.supplier_code} onChange={set('supplier_code')} className={INPUT} /></Lbl>
        <Lbl t="Unit"><input value={f.unit} onChange={set('unit')} className={INPUT} /></Lbl>
        <Lbl t="Par level"><input inputMode="decimal" value={f.par_level} onChange={set('par_level')} className={INPUT} /></Lbl>
        <Lbl t="Storage area"><input value={f.storage_area} onChange={set('storage_area')} className={INPUT} /></Lbl>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={reject} className="flex items-center gap-1 rounded-xl border border-brand-border px-3 py-2 text-sm text-brand-ink"><X size={15} /> Reject</button>
        <button onClick={approve} disabled={busy} className="flex items-center gap-1 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"><Check size={15} /> Approve & add</button>
      </div>
    </div>
  )
}

function Lbl({ t, children }) {
  return <label className="block"><span className="mb-0.5 block text-[11px] font-medium text-brand-muted">{t}</span>{children}</label>
}
